import axios from "axios";
import throttle from "lodash-es/throttle";
import { useQuery } from "@tanstack/react-query";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  query,
  where,
  limit,
  orderBy,
  getDocs,
  collection,
  getFirestore,
} from "firebase/firestore";
import { RootState } from "app/store";
import { LocalStorageKey, Asset } from "app/types";
import { fetchAuthToken, parentsFirebase } from "app/Auth";
import Constants from "common/constant";
import { client } from "common/apiClient";
import { UserType, ChatRoomType } from "./types";

export interface IconMetadata {
  source: string;
  character?: string;
  style?: string;
  expression?: string;
  accessory?: string;
  background?: string;
  file_size?: number;
  // アップロード用フィールド
  filename?: string;
  content_type?: string;
  width?: string;
  height?: string;
}

export const createGuestAccount = async ({
  user,
}: {
  user: {
    icon?: string;
    display_name: string;
    gender?: string;
    locale: string;
    env: string;
    // プロフィール情報
    birthday?: string;
    favorite_color?: string[];
    best_subject?: string[];
    glasses?: string;
    accessory?: string[];
    hairstyle?: string;
    body_type?: string;
    face_shape?: string;
    self_introduction?: string;
    // AI生成アイコンのメタ情報
    icon_metadata?: IconMetadata;
  };
}) => {
  return client({ token: Constants.apiAuthKey }).post("/v1/user/sign_up/guest", user);
};

export type CreateParentAccountResponse = {
  parent?: { uid: string; email?: string };
  claim?: {
    status: "claimed" | "already_owned" | "quota_exceeded" | "skipped";
    child_uid: string | null;
  };
  plan?: string;
};

export const createParentAccount = async ({
  user,
}: {
  user: {
    uid: string;
    child_uid?: string;
    email: string;
    provider: string;
    display_name: string;
    language: {
      locale: string;
      format: string;
    };
    env: string;
    timezone?: string;
    country?: string;
  };
}) => {
  return client({ token: Constants.apiAuthKey }).post<CreateParentAccountResponse>(
    "/v1/user/sign_up/parent",
    user,
  );
};

export const createChildAccount = createAsyncThunk(
  "user/createChildAccount",
  async (
    {
      user,
    }: {
      user: {
        parent_uid: string;
        display_name: string;
        locale: string;
        env: string;
        // プロフィール情報
        icon?: string;
        birthday?: string;
        gender?: string;
        favorite_color?: string[];
        best_subject?: string[];
        glasses?: string;
        accessory?: string[];
        hairstyle?: string;
        body_type?: string;
        face_shape?: string;
        self_introduction?: string;
        // AI生成アイコンのメタ情報
        icon_metadata?: IconMetadata;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).post("/v1/user/account/child", user);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return rejectWithValue({
          code: error.response.data?.code || "QUOTA_EXCEEDED",
          message: error.response.data?.message || "プレイヤー数の上限に達しました",
        });
      }
      throw error;
    }
  },
);

export const getAccountUsers = createAsyncThunk(
  "user/getAccountUsers",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/user/account/${uid}`);
    return response.data;
  },
);

export const userStartPlay = createAsyncThunk(
  "user/userStartPlay",
  async ({ parent_uid, uid, env }: { parent_uid: string; uid: string; env: string }) => {
    const token = await fetchAuthToken();
    await client({ token: token }).put(
      `/v1/user/account/play?parent_uid=${parent_uid}&uid=${uid}&env=${env}`,
    );
  },
);

export const updateLastSignInTime = (): void => {
  const currentTime = new Date().toISOString();
  localStorage.setItem(LocalStorageKey.LAST_SIGN_AT, currentTime);
};

export const updateAccount = createAsyncThunk(
  "user/updateAccount",
  throttle(
    async ({
      uid,
      userInfo,
    }: {
      uid: string;
      userInfo: {
        icon?: string;
        display_name?: string;
      };
    }) => {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).put(
        `/v1/user/account/parent/${uid}`,
        userInfo,
      );
      return response.data;
    },
    1000,
  ),
);

export interface ProfileData {
  birthday?: string;
  gender?: string;
  locale?: string;
  // 容姿
  glasses?: string;
  hairstyle?: string;
  body_type?: string;
  face_shape?: string;
  // お勤め先
  company_size?: string;
  industry?: string;
  occupation?: string;
  job_title?: string;
  position?: string;
  organization?: string;
  // 住所
  is_overseas?: boolean;
  region?: string;
  city?: string;
  // 家族
  family_structure?: string;
  family_roles?: string[];
  children_count?: number;
  boys_count?: number;
  girls_count?: number;
  spouse?: boolean;
  // その他
  annual_income?: string;
  self_introduction?: string;
}

export const updateUser = createAsyncThunk(
  "user/updateUser",
  throttle(
    async ({
      uid,
      userInfo,
    }: {
      uid: string;
      userInfo: {
        icon?: string;
        name?: string;
        birthday?: string;
        gender?: string;
        favorite_color?: string[];
        best_subject?: string[];
        glasses?: string;
        accessory?: string[];
        hairstyle?: string;
        body_type?: string;
        face_shape?: string;
        self_introduction?: string;
      };
    }) => {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).put(
        `/v1/user/account/child/${uid}`,
        userInfo,
      );
      return response.data;
    },
    1000,
  ),
);

export const deleteAccount = async ({
  uid,
  options,
  detail,
}: {
  uid: string;
  options: string[];
  detail: string;
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).delete(`/v1/user/account/parent/${uid}`, {
    data: {
      options,
      detail,
    },
  });
};

export const deleteChild = createAsyncThunk(
  "user/deleteChild",
  async ({ uid, parent_uid }: { uid: string; parent_uid: string }) => {
    const token = await fetchAuthToken();
    await client({ token: token }).delete(`/v1/user/account/child/${uid}?parent_uid=${parent_uid}`);
    return uid;
  },
);

export const updateChildStatus = createAsyncThunk(
  "user/updateChildStatus",
  async ({ uid, type }: { uid: string; type: UserType }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).put(`/v1/user/account/child/${uid}/status`, { type });
    return response.data;
  },
);

export const getGroupUsers = createAsyncThunk(
  "user/getGroupUsers",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/user/${uid}/group/member`);
    return response.data;
  },
);

export const fetchUserStatus = async ({ uid }: { uid: string }) => {
  const token = await fetchAuthToken();
  return client({ token: token }).get(`/v1/user/status/${uid}`);
};

export const useUserStatus = (uid: string) => {
  return useQuery({
    queryKey: ["user/status", uid],
    queryFn: async () => {
      const response = await fetchUserStatus({ uid });
      return response.data.data;
    },
    enabled: !!uid,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュを有効とする
    gcTime: 10 * 60 * 1000, // 10分間はキャッシュを保持
  });
};

export const getUserStatus = createAsyncThunk(
  "user/getUserStatus",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/user/status/${uid}`);
    return response.data.data;
  },
);

export const getUserHistory = createAsyncThunk(
  "user/getUserHistory",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/user/history/${uid}`);
    return response.data.data;
  },
);

export const markChatRoomMessage = createAsyncThunk<any, void>(
  "user/markChatRoomMessage",
  async (_, thunkAPI) => {
    const token = await fetchAuthToken();
    const { user } = thunkAPI.getState() as RootState;
    const chatRoomsReadAt = user.chatRoomMessageSync.ids
      .map((id) => user.chatRoomMessageSync.entities[id])
      .filter(
        (sync) =>
          sync.lastReadAt !== sync.latestReadAt ||
          (sync.lastReadAt === null && sync.latestReadAt !== null),
      )
      .map((sync) => ({
        chat_room_id: sync.id,
        uid: sync.uid,
        last_read_at: sync.latestReadAt,
      }));
    const response = await client({ token: token }).put(`/v1/chat/room/marked`, {
      chatRoomsReadAt,
    });
    return response.data.data;
  },
);

export const getUserChatRooms = createAsyncThunk(
  "user/getUserChatRooms",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(`/v1/chat/room/${uid}`);
    return response.data.data;
  },
);

export const getChatRoomMessages = createAsyncThunk(
  "user/getChatRoomMessages",
  async (
    {
      roomId,
      lastMessageSyncAt,
      messageCount,
      chatRoomType,
    }: {
      roomId: string;
      lastMessageSyncAt: string;
      messageCount: number;
      chatRoomType: ChatRoomType;
    },
    thunkAPI,
  ) => {
    const state = thunkAPI.getState() as RootState;
    const db = getFirestore(parentsFirebase);

    // talk room 以外は制限チェックしない
    if (chatRoomType !== ChatRoomType.TALK) {
      const queryMessage = query(
        collection(db, "rooms", roomId, "messages"),
        where("created_at", "<", lastMessageSyncAt),
        orderBy("created_at", "desc"),
        limit(messageCount),
      );
      const querySnapshot = await getDocs(queryMessage);
      const messages = querySnapshot.docs.map((doc) => doc.data());
      return { messages, limitReached: false };
    }

    // 以下は talk room の場合のみ（制限対象）
    const chatHistoryLimit = state.subscription.accountLimits.chatHistoryLimit;
    const limitValue = chatHistoryLimit?.limit ?? -1;
    const used = chatHistoryLimit?.used ?? 0;
    const unlimited = chatHistoryLimit?.unlimited ?? false;

    // 制限チェック（-1 は無制限、unlimited も無制限）
    if (!unlimited && limitValue !== -1 && used >= limitValue) {
      return { messages: [], limitReached: true };
    }

    const queryMessage = query(
      collection(db, "rooms", roomId, "messages"),
      where("created_at", "<", lastMessageSyncAt),
      orderBy("created_at", "desc"),
      limit(messageCount),
    );
    const querySnapshot = await getDocs(queryMessage);
    const messages = querySnapshot.docs.map((doc) => doc.data());

    return { messages, limitReached: false };
  },
);

export const sendChatRoomMessage = async ({ roomId, message }: { roomId: string; message: {} }) => {
  const token = await fetchAuthToken();
  return client({ token: token }).post(`/v1/chat/room/message/${roomId}`, message);
};

export const sendContactChatRoomMessage = async ({
  uid,
  roomId,
  message,
}: {
  uid: string;
  roomId: string;
  message: {};
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).post(`/v1/contacts/message/${uid}/${roomId}`, message);
};

export type AssetsResponse = {
  assets: Asset[];
  totalSize: number;
};

export const getUserAssets = async ({ uid }: { uid: string }): Promise<AssetsResponse> => {
  const token = await fetchAuthToken();
  const response = await client({ token: token }).get(`/v1/asset/user/${uid}/files`);
  return response.data;
};
